import { FileIcon } from '@/assets/icons'
import Modal from '@components/Modal'
import { useFiles } from '@hooks'
import { GetFileType } from '@orbit/api-types'
import toast from 'react-hot-toast'
import React, { useMemo, useState } from 'react'
import FileViewer from '@components/FileViewer/FileViewer'
import {
	EditButton,
	ViewerDocIcon,
	ViewerFileCard,
	ViewerFileName,
	ViewerFooter,
	ViewerGrid,
	ViewerThumbnail,
} from './AttachmentsViewerModal.styles'
import { EmptyNote } from '@components/EmptyState'
import { iconSizes } from '@/theme/tokens'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic']

function isImage(file: GetFileType): boolean {
	return IMAGE_MIME_TYPES.includes(file.mimeType)
}

function getFileExtension(fileName: string): string {
	const parts = fileName.split('.')
	return parts.length > 1 ? parts[parts.length - 1] : ''
}

interface AttachmentsViewerModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	fileIds: string[]
	title?: string
	onEditClick?: () => void
}

const AttachmentsViewerModal: React.FC<AttachmentsViewerModalProps> = ({
	isOpen,
	onClose,
	tripId,
	fileIds,
	title = 'Attachments',
	onEditClick,
}) => {
	const { data: allFilesData } = useFiles(tripId)
	const [lightboxFile, setLightboxFile] = useState<GetFileType | null>(null)

	const attachments = useMemo(() => {
		if (!allFilesData?.files || fileIds.length === 0) return []
		return allFilesData.files.filter(f => fileIds.includes(f.id))
	}, [allFilesData?.files, fileIds])

	const handleFileClick = (file: GetFileType) => {
		if (isImage(file)) {
			setLightboxFile(file)
		} else {
			window.open(file.url, '_blank')
		}
	}

	const handleEditClick = () => {
		onClose()
		onEditClick?.()
	}

	const footer = onEditClick ? (
		<ViewerFooter>
			<EditButton onClick={handleEditClick}>Edit Attachments</EditButton>
		</ViewerFooter>
	) : undefined

	return (
		<>
			<Modal
				isOpen={isOpen && !lightboxFile}
				onClose={onClose}
				title={title}
				size="large"
				footer={footer}
			>
				{attachments.length === 0 ? (
					<EmptyNote>No attachments</EmptyNote>
				) : (
					<ViewerGrid>
						{attachments.map(file => (
							<ViewerFileCard key={file.id} onClick={() => handleFileClick(file)}>
								<ViewerThumbnail>
									{isImage(file) ? (
										<img src={file.url} alt={file.fileName} loading="lazy" />
									) : (
										<ViewerDocIcon>
											<FileIcon sx={{ fontSize: iconSizes.xl }} />
											<span>{getFileExtension(file.fileName)}</span>
										</ViewerDocIcon>
									)}
								</ViewerThumbnail>
								<ViewerFileName title={file.fileName}>{file.fileName}</ViewerFileName>
							</ViewerFileCard>
						))}
					</ViewerGrid>
				)}
			</Modal>

			<FileViewer file={lightboxFile} onClose={() => setLightboxFile(null)} onDownloadError={() => toast.error('Failed to download file')} />
		</>
	)
}

export default AttachmentsViewerModal
